import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface ModalState {
  isOpen: boolean;
  type: string | null;
  data?: unknown;
}

export interface ModalContextValue {
  modalState: ModalState;
  openModal: (type: string, data?: unknown) => void;
  closeModal: () => void;
}

const CLOSED_STATE: ModalState = { isOpen: false, type: null, data: undefined };

const ModalContext = createContext<ModalContextValue | null>(null);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modalState, setModalState] = useState<ModalState>(CLOSED_STATE);

  const openModal = useCallback((type: string, data?: unknown) => {
    setModalState((prev) => {
      if (prev.isOpen) {
        // Close first, then open — matches original two-step jotai behavior.
        // React batches these into a single render, but the intermediate
        // closed state is observable inside the updater for correctness.
        return { isOpen: true, type, data };
      }
      return { isOpen: true, type, data };
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState((prev) => {
      if (!prev.isOpen) return prev;
      return CLOSED_STATE;
    });
  }, []);

  const value = useMemo<ModalContextValue>(
    () => ({ modalState, openModal, closeModal }),
    [modalState, openModal, closeModal],
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

export function useModalContext(): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (ctx === null) {
    throw new Error("useModalContext must be used within a ModalProvider");
  }
  return ctx;
}
